import { Fieldset, Group, Select, Stack, Switch, Text, TextInput } from '@mantine/core';
import HelpPopover from '../../HelpPopover/HelpPopover';
import KeyValueMapper from '../../KeyValueMapper';
import { errorWhenIsPathOnly, MAX_FILTER_ROWS } from '../connectorConfig';
import { HELP } from './workspaceHelp';
import classes from './workspace.module.css';

const MATCHES = [
  { value: 'equals', label: 'equals' },
  { value: 'notEquals', label: 'not equals' },
];

const strictColumn = (readOnly) => ({
  label: (
    <Group gap={2} wrap="nowrap">
      Strict <HelpPopover {...HELP.strict} />
    </Group>
  ),
  render: (row, index, setRow) => (
    <Switch
      aria-label={index === 0 ? 'Strict' : `Strict ${index + 1}`}
      size="xs"
      disabled={readOnly}
      checked={!!row.strict}
      onChange={(event) => setRow({ ...row, strict: event.currentTarget.checked })}
    />
  ),
});

// The rules a Send can end in (C7 `rule`); the one that fired is filled.
export const RULES = [
  { value: 'ok', label: 'Success' },
  { value: 'notFound', label: 'Not found' },
  { value: 'filterEmpty', label: 'Filter kept no rows' },
  { value: 'error', label: 'Error' },
];

export const RuleDot = ({ fired, children }) => (
  <span className={classes.rule} data-fired={fired || undefined}>
    {children}
  </span>
);

// Filter rows (each with its own Strict), not found when and error when: the config's three rules.
// `fired` is the rule of the last Send, marked on its own row.
const RulesPanel = ({ form, setField, errors = {}, readOnly, fired }) => {
  const addFilter = (rule) => {
    const before = form.filterRows;
    setField('filterRows', [...before, { ...rule, strict: false }].slice(0, MAX_FILTER_ROWS));
    return () => setField('filterRows', before);
  };

  return (
    <Stack gap="xs">
      <Group gap={4}>
        <RuleDot fired={fired === 'filterEmpty'}>
          <Text size="sm" fw={600} c="var(--mantine-color-text)">
            Filter rows
          </Text>
        </RuleDot>
        <HelpPopover {...HELP.rules} onUse={readOnly ? undefined : addFilter} />
      </Group>
      {/* KeyValueMapper has no disabled prop; a disabled fieldset turns off every control inside. */}
      <Fieldset variant="unstyled" disabled={readOnly}>
        <KeyValueMapper
          rows={form.filterRows}
          setRows={(rows) => setField('filterRows', rows)}
          keyLabel="Response path"
          valueLabel="Equals"
          keyPlaceholder="city"
          valuePlaceholder="{{args.city}}"
          addLabel="Add filter"
          maxRows={readOnly ? 0 : MAX_FILTER_ROWS}
          extraColumn={strictColumn(readOnly)}
        />
      </Fieldset>
      {errors.filter && (
        <Text size="xs" c="red">
          {errors.filter}
        </Text>
      )}

      <Group gap="xs" grow align="flex-start">
        <TextInput
          label={<RuleDot fired={fired === 'notFound'}>Not found when</RuleDot>}
          size="xs"
          ff="monospace"
          placeholder="data.items"
          disabled={readOnly}
          value={form.notFoundPath}
          onChange={(event) => setField('notFoundPath', event.target.value)}
        />
        <TextInput
          label="equals"
          size="xs"
          ff="monospace"
          placeholder="[]"
          disabled={readOnly}
          value={form.notFoundEquals}
          onChange={(event) => setField('notFoundEquals', event.target.value)}
        />
      </Group>

      <Group gap="xs" grow align="flex-start">
        <TextInput
          label={<RuleDot fired={fired === 'error'}>Error when</RuleDot>}
          size="xs"
          ff="monospace"
          placeholder="status"
          disabled={readOnly}
          error={errors.errorWhenPath}
          value={form.errorWhenPath}
          onChange={(event) => setField('errorWhenPath', event.target.value)}
        />
        <Select
          label="matches"
          size="xs"
          data={MATCHES}
          allowDeselect={false}
          disabled={readOnly}
          value={form.errorWhenMode}
          onChange={(value) => setField('errorWhenMode', value || 'equals')}
        />
        <TextInput
          label="value"
          size="xs"
          ff="monospace"
          placeholder='"error"'
          disabled={readOnly}
          error={errors.errorWhenValue}
          value={form.errorWhenValue}
          onChange={(event) => setField('errorWhenValue', event.target.value)}
        />
      </Group>
      {errorWhenIsPathOnly(form) && (
        <Text size="xs" c="dimmed">
          The stored rule compares against no value; it is saved back as it is until a value is typed.
        </Text>
      )}
    </Stack>
  );
};

export default RulesPanel;
